import { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/ApiConfig';
import TableContainer from '../TableContainer';
import Pagination10 from '../Pagination10';
import useDefaultModal from '../hooks/useDefaultModal';
import useConfirmModal from '../hooks/useConfirmModal';
import ConfirmModal from './ConfirmModal';
import DefaultModal from './DefaultModal';
import ResponsiveShadow from '../ResponsiveShadow';
import usePagination10 from '../hooks/usePagination10';
import PageHeader from '../PageHeader';
import PageContent from '../PageContent';
import ArpItemModal from './ArpItemModal';
import DataTable from '../DataTable';
import PaginationBar from '../PaginationBar';
import { formatQuantidade } from '../../../service/utils/formatQuantidade';

function ArpItensModal({ isOpen, onClose, arp, possuiAditivo, onSave }) {
  const [arpItens, setArpItens] = useState([]);
  const [filtro, setFiltro] = useState('');
  const [atualizar, setAtualizar] = useState(false);
  const [arpItemSelecionado, setArpItemSelecionado] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [possuiOrdens, setPossuiOrdens] = useState(false);
  const [editarItem, setEditarItem] = useState('N');
  const token = localStorage.getItem('token');
  const [isLoading, setLoading] = useState(true);
  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const { setConfirmModal, showConfirmModal } = useConfirmModal();
  const user = JSON.parse(localStorage.getItem('user'));
  const isAdmin = user?.role === 'ADMIN';

  useEffect(() => {
    if (arp && arp.id) {
      carregarItens(arp.id);
      verificarOrdens(arp.id);
      carregarParametro(arp.idEntidade);
    }
  }, [arp, atualizar]);

  function carregarItens(idArp) {
    setLoading(true);
    axios
      .get(`${API_BASE_URL.ARP_ITEM}/arp/${idArp}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(result => setArpItens(result.data))
      .catch(error => {
        console.error('Erro ao obter itens da ARP:', error);
        showDefaultModal('error', 'Erro ao carregar itens da ARP.');
      })
      .finally(() => setLoading(false));
  }

  function verificarOrdens(idArp) {
    axios
      .get(`${API_BASE_URL.ARP_ORDEM}/arp/${idArp}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(result => setPossuiOrdens(result.data.length > 0))
      .catch(error => console.error('Erro ao verificar ordens:', error));
  }

  function carregarParametro(idEntidade) {
    axios
      .get(`${API_BASE_URL.PARAMETRO}/entidade/${idEntidade}/map`, { headers: { Authorization: `Bearer ${token}` } })
      .then(result => setEditarItem(result.data?.EDITAR_ITEM || 'N'))
      .catch(() => setEditarItem('N'));
  }

  function handleDelete(arpItem) {
    if (possuiOrdens) {
      showDefaultModal('error', 'Os itens não podem ser editados pois existem ordens associadas.');
      return;
    }
    showConfirmModal('delete', () => handleConfirmDelete(arpItem));
  }

  function handleConfirmDelete(arpItem) {
    axios
      .delete(`${API_BASE_URL.ARP_ITEM}/${arp.id}/${arpItem.id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => {
        showDefaultModal('success', 'Item excluído com sucesso!');
        setAtualizar(prev => !prev);
      })
      .catch(error => {
        console.error('Erro ao excluir item:', error);
        showDefaultModal('error', 'Erro ao excluir o item!');
      });
  }

  function handleEdit(arpItem) {
    setArpItemSelecionado(arpItem);
    setShowModal(true);
  }

  function handleNew() {
    setArpItemSelecionado(null);
    setShowModal(true);
  }

  function handleCloseModal() {
    setShowModal(false);
    setArpItemSelecionado(null);
  }

  function handleSaveSuccess() {
    setAtualizar(prev => !prev);
    setArpItemSelecionado(null);
    if (onSave) onSave();
  }

  function handleExportExcel() {
    if (!arp) return;
    setLoading(true);
    axios
      .post(`${API_BASE_URL.ARP_ITEM}/exportExcel/${arp.id}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      })
      .then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `arp_${arp.numArp}_${arp.ano}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        showDefaultModal('success', 'Arquivo exportado com sucesso!');
      })
      .catch(error => {
        console.error('Erro ao exportar:', error);
        showDefaultModal('error', 'Erro ao exportar os itens!');
      })
      .finally(() => setLoading(false));
  }

  function handleImportExcel(event) {
    if (!arp) return;
    if (possuiOrdens) {
      showDefaultModal('error', 'Os itens não podem ser editados pois existem ordens associadas.');
      event.target.value = '';
      return;
    }
    const file = event.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('id', arp.id);
    setLoading(true);
    axios
      .post(`${API_BASE_URL.ARP_ITEM}/importExcel?id=${arp.id}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      })
      .then(response => {
        showDefaultModal('success', response.data);
        setAtualizar(prev => !prev);
      })
      .catch(error => {
        console.error('Erro ao importar arquivo:', error);
        const errorMessage = error.response?.data || 'Erro ao importar o arquivo!';
        showDefaultModal('error', errorMessage);
      })
      .finally(() => {
        setLoading(false);
        event.target.value = '';
      });
  }

  function handleImportJson(event) {
    if (!arp) return;
    if (possuiOrdens) {
      showDefaultModal('error', 'Os itens não podem ser editados pois existem ordens associadas.');
      event.target.value = '';
      return;
    }
    const file = event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (e) => {
      let data;
      try {
        data = JSON.parse(e.target.result);
      } catch {
        showDefaultModal('error', 'Arquivo JSON inválido.');
        return;
      }
      if (!Array.isArray(data) || data.length === 0) {
        showDefaultModal('error', 'O JSON deve ser um array não vazio.');
        return;
      }
      setLoading(true);
      let successCount = 0;
      let errorCount = 0;
      for (const item of data) {
        try {
          await axios.post(`${API_BASE_URL.ARP_ITEM}`, {
            idArp: arp.id,
            id: item.id,
            descricao: item.descricao,
            marca: item.marca,
            unidade: item.unidade,
            quantidade: item.quantidade,
            valorUnit: item.valorUnit
          }, { headers: { Authorization: `Bearer ${token}` } });
          successCount++;
        } catch {
          errorCount++;
        }
      }
      setLoading(false);
      setAtualizar(prev => !prev);
      if (errorCount === 0) {
        showDefaultModal('success', `${successCount} item(ns) importado(s) com sucesso!`);
      } else {
        showDefaultModal('error', `${successCount} importado(s) com sucesso, ${errorCount} com erro.`);
      }
      event.target.value = '';
    };
    reader.readAsText(file);
  }

  const normalizeString = str =>
    str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase();

  const itensFiltrados = Array.isArray(arpItens)
    ? arpItens.filter(item => {
        const b = normalizeString(filtro);
        return (
          normalizeString(item.descricao || '').includes(b) ||
          normalizeString(item.marca || '').includes(b) ||
          normalizeString(item.unidade || '').includes(b)
        );
      })
    : [];

  const totalItems = itensFiltrados.length;
  const { setPagination } = usePagination10(totalItems);
  const currentItems = itensFiltrados.slice(setPagination.startIndex, setPagination.startIndex + setPagination.rowsPerPage);

  const calcularTotalGeral = () =>
    itensFiltrados.reduce((total, item) => total + ((item.quantidade || 0) * (item.valorUnit || 0)), 0);

  const bloquearEdicao = possuiOrdens && editarItem !== 'S';
  const bloquearPorAditivo = !!possuiAditivo;
  const bloquearTudo = bloquearPorAditivo || possuiOrdens;

  if (!isOpen) return null;

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg" style={{ maxWidth: '95vw', height: '90vh' }}>
          <div className="modal-content h-100 overflow-hidden">
          <div
            className="modal-header text-white"
            style={{
              background: 'linear-gradient(45deg, rgb(7, 29, 65) 0%, rgba(29, 100, 216, 1) 100%)',
              padding: '0.5rem 1rem',
              borderTopLeftRadius: '0.5rem',
              borderTopRightRadius: '0.5rem',
              border: 'none',
            }}
          >
            <h6 className="modal-title mb-0">
              <i className="bi bi-list-ul me-2"></i>
              Itens da ARP {arp?.numArp}
            </h6>
            <button type="button" className="btn-close btn-close-white" onClick={onClose} title="Fechar"></button>
          </div>
          <PageContent>
            <div className="d-flex flex-column flex-lg-row align-items-start align-items-lg-center gap-2 mb-2">
              <div className="botao-grid-2col">
                <button
                  className="btn btn-success btn-sm botao-igual"
                  onClick={handleNew}
                  disabled={bloquearTudo}
                  title={bloquearPorAditivo ? 'Bloqueado - os itens são gerenciados no aditivo' : (possuiOrdens ? 'Bloqueado - existem ordens associadas' : 'Novo item')}
                >
                  <i className="bi bi-plus-circle me-1"></i>Novo
                </button>
                <button
                  className="btn btn-primary btn-sm botao-igual"
                  disabled={!arpItemSelecionado || bloquearEdicao || bloquearPorAditivo}
                  onClick={() => handleEdit(arpItemSelecionado)}
                  title={bloquearPorAditivo ? 'Bloqueado - os itens são gerenciados no aditivo' : (bloquearEdicao ? 'Bloqueado por parâmetro' : !arpItemSelecionado ? 'Selecione um item' : 'Editar item selecionado')}
                >
                  <i className="bi bi-pencil me-1"></i>Editar
                </button>
                <button
                  className="btn btn-danger btn-sm botao-igual"
                  disabled={!arpItemSelecionado || bloquearTudo}
                  onClick={() => handleDelete(arpItemSelecionado)}
                  title={bloquearPorAditivo ? 'Bloqueado - os itens são gerenciados no aditivo' : (possuiOrdens ? 'Bloqueado - existem ordens associadas' : !arpItemSelecionado ? 'Selecione um item' : 'Excluir item selecionado')}
                >
                  <i className="bi bi-trash me-1"></i>Excluir
                </button>
                <button
                  className="btn btn-secondary btn-sm botao-igual"
                  onClick={handleExportExcel}
                  title="Exportar itens da ARP para Excel"
                >
                  <i className="bi bi-file-earmark-arrow-down me-1"></i>Export
                </button>
                <label
                  className={`btn btn-secondary btn-sm botao-igual ${bloquearTudo ? 'disabled' : ''}`}
                  title={bloquearPorAditivo ? 'Bloqueado - os itens são gerenciados no aditivo' : (possuiOrdens ? 'Bloqueado - existem ordens associadas' : 'Importar itens via Excel')}
                  style={{ cursor: bloquearTudo ? 'not-allowed' : 'pointer' }}
                >
                  <i className="bi bi-file-earmark-arrow-up me-1"></i>Import
                  <input
                    type="file"
                    accept=".xlsx, .xls"
                    style={{ display: 'none' }}
                    onChange={handleImportExcel}
                    disabled={bloquearTudo}
                  />
                </label>
                {isAdmin && (
                  <label className={`btn btn-secondary btn-sm botao-igual${bloquearTudo ? ' disabled' : ''}`}
                    title={bloquearPorAditivo ? 'Bloqueado - os itens são gerenciados no aditivo' : (possuiOrdens ? 'Bloqueado - existem ordens associadas' : 'Importar itens via JSON')}
                    style={{ cursor: bloquearTudo ? 'not-allowed' : 'pointer' }}>
                    <i className="bi bi-file-earmark-arrow-up me-1"></i>Import JSON
                    <input
                      type="file"
                      accept=".json,application/json"
                      style={{ display: 'none' }}
                      onChange={handleImportJson}
                      disabled={bloquearTudo}
                    />
                  </label>
                )}
              </div>
              <div className="col-12 col-lg-2 ms-lg-auto">
                <div className="input-group input-group-sm">
                  <span className="input-group-text"><i className="bi bi-search"></i></span>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Buscar..."
                    title="Buscar por descrição, marca, unidade"
                    value={filtro}
                    onChange={e => setFiltro(e.target.value.toUpperCase())}
                  />
                </div>
              </div>
            </div>

            <TableContainer isLoading={isLoading}>
              <DataTable style={{ fontSize: '0.8rem' }} head={
                <tr>
                  <th scope="col" className="px-2 text-center align-middle">Nº</th>
                  <th scope="col" className="px-2 text-center align-middle">Descrição</th>
                  <th scope="col" className="px-2 text-center align-middle">Marca</th>
                  <th scope="col" className="px-2 text-center align-middle">Unidade</th>
                  <th scope="col" className="px-2 text-center align-middle">Quantidade</th>
                  <th scope="col" className="px-2 text-center align-middle">Saldo Qtde</th>
                  <th scope="col" className="px-2 text-center align-middle">Valor Unit.</th>
                  <th scope="col" className="px-2 text-center align-middle">Total</th>
                  <th scope="col" className="px-2 text-center align-middle">Saldo Total</th>
                </tr>}>
                <tbody>
                  {currentItems.map(item => (
                      <tr
                        key={item.id}
                        className={arpItemSelecionado && arpItemSelecionado.id === item.id ? 'table-primary' : ''}
                        style={{ cursor: 'pointer' }}
                        onClick={() => setArpItemSelecionado(item)}
                      >
                        <td className="px-2">{item.id}</td>
                        <td className="px-2">{item.descricao}</td>
                        <td className="px-2">{item.marca}</td>
                        <td className="px-2">{item.unidade}</td>
                        <td className="px-2 text-end">{formatQuantidade(item.quantidade)}</td>
                        <td className="px-2 text-end fw-semibold text-success">
                          {formatQuantidade((item.quantidade || 0) - (item.qtdePedida || 0))}
                        </td>
                        <td className="px-2 text-end">
                          {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(item.valorUnit || 0)}
                        </td>
                        <td className="px-2 text-end">
                          {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format((item.quantidade || 0) * (item.valorUnit || 0))}
                        </td>
                        <td className="px-2 text-end fw-semibold text-success">
                          {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(((item.quantidade || 0) - (item.qtdePedida || 0)) * (item.valorUnit || 0))}
                        </td>
                      </tr>
                    ))}
                  {currentItems.length === 0 && (
                    <tr>
                      <td colSpan="9" className="text-center py-3 text-muted">
                        <i className="bi bi-inbox fs-3 d-block mb-2"></i>
                        Nenhum item encontrado
                      </td>
                    </tr>
                  )}
                </tbody>
                {itensFiltrados.length > 0 && (
                  <tfoot className="fw-bold" style={{ position: 'sticky', bottom: 0, zIndex: 2, backgroundColor: '#f8f9fa', isolation: 'isolate' }}>
                      <tr>
                        <td colSpan="2" className="px-2 text-start">
                          {(possuiAditivo || possuiOrdens) && (
                            <span className={`p-1 d-block ${(bloquearPorAditivo || bloquearEdicao) ? 'bg-danger text-white' : 'bg-warning'}`} style={{ fontSize: '0.8rem', borderRadius: '4px' }}>
                              <i className="bi bi-exclamation-triangle-fill me-1"></i>
                              {bloquearPorAditivo
                                ? 'Os itens da ARP são gerenciados no aditivo. Edite-os no aditivo.'
                                : (bloquearEdicao
                                  ? 'Os itens não podem ser editados pois existem ordens associadas.'
                                  : 'Os itens podem ser editados mas existem ordens associadas que podem causar problemas.')}
                            </span>
                          )}
                        </td>
                        <td colSpan="5" className="px-2 text-end">TOTAL GERAL:</td>
                        <td className="px-2 text-end">
                          {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(calcularTotalGeral())}
                        </td>
                        <td className="px-2 text-end fw-semibold text-success">
                          {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
                            itensFiltrados.reduce((total, item) => total + (((item.quantidade || 0) - (item.qtdePedida || 0)) * (item.valorUnit || 0)), 0)
                          )}
                        </td>
                      </tr>
                  </tfoot>
                )}
              </DataTable>
            </TableContainer>
          </PageContent>
          <PaginationBar setPagination={setPagination} />
        </div>
      </div>

      {showModal && (
        <ArpItemModal show={showModal} arpItem={arpItemSelecionado} arp={arp} onClose={handleCloseModal} onSave={handleSaveSuccess} />
      )}

      <ConfirmModal {...setConfirmModal} />
      <DefaultModal {...setDefaultModal} />
    </div>
  );
}

export default ArpItensModal;
